"use client";

import { AlertCircle, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";

interface UnsavedChangesBannerProps {
    hasUnsavedChanges: boolean;
    onUndo: () => void;
    onSave: () => void;
    isSaving?: boolean;
    title?: string;
    description?: string;
    undoLabel?: string;
    saveLabel?: string;
    className?: string;
}

export function UnsavedChangesBanner({
    hasUnsavedChanges,
    onUndo,
    onSave,
    isSaving = false,
    title = "Unsaved Changes",
    description = "You have unsaved changes. Please save your changes to avoid losing them.",
    undoLabel = "Undo Changes",
    saveLabel = "Save Changes",
    className = "",
}: UnsavedChangesBannerProps) {
    return (
        <div
            className={`transition-all duration-300 ease-in-out transform ${hasUnsavedChanges
                ? "opacity-100 translate-y-0 h-auto min-h-[80px]"
                : "opacity-0 -translate-y-4 h-0 overflow-hidden pointer-events-none"
                } ${className}`}
        >
            <Alert
                variant="default"
                className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950 dark:border-yellow-700 shadow-lg"
            >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-2">
                    <div className="flex items-start sm:items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-500 dark:text-yellow-400 mt-0.5 sm:mt-0" />
                        <div>
                            <AlertTitle className="text-yellow-700 dark:text-yellow-200">
                                {title}
                            </AlertTitle>
                            <AlertDescription className="text-yellow-600 dark:text-yellow-300">
                                {description}
                            </AlertDescription>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onUndo}
                            type="button"
                            className="text-yellow-700 hover:bg-yellow-100 dark:text-yellow-200 dark:hover:bg-yellow-900 dark:border-yellow-600 transition-colors duration-200"
                        >
                            {undoLabel}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={onSave}
                            className="flex items-center gap-2"
                            disabled={isSaving}
                        >
                            <Save className="w-4 h-4" />
                            {isSaving ? "Saving..." : saveLabel}
                        </Button>
                    </div>
                </div>
            </Alert>
        </div>
    );
}


